import { APP_LABELS } from "@/shared/dashboard/workspace/catalog";
import { useState } from "react";
import { useLanguage } from "@/shared/i18n/LanguageContext";
import { useVertical } from "@/shared/contexts/VerticalContext";
import { Icon } from "@/shared/dashboard/shell/Icon";
import {
  hasSeenDesktopHint,
  openDesktopApp,
} from "@/shared/dashboard/desktopMode";
import { registerWidget } from "@/shared/dashboard/widgets/registry";
import type { WidgetProps } from "@/shared/dashboard/widgets/types";

const ICON_STYLES: Record<string, string> = {
  create: "from-[#DAA84E] to-[#AA6B1D]",
  "conversation-maps": "from-[#5578B5] to-[#354E88]",
  patients: "from-[#4B8F88] to-[#2D6963]",
  "upload-recording": "from-[#D07A54] to-[#A44C31]",
  recordings: "from-[#538FAC] to-[#315F7B]",
  notes: "from-[#9B78A6] to-[#6D527A]",
  "aar-writer": "from-[#315F8C] to-[#173957]",
  "case-files": "from-[#6E7F91] to-[#394958]",
  files: "from-[#D39A3F] to-[#A76B1D]",
  courses: "from-[#7776A8] to-[#515181]",
  progress: "from-[#5F9774] to-[#35684B]",
  calendar: "from-[#D36962] to-[#A83F3B]",
};

// Shared with the customizable workspace tool library.

const LAUNCHER_LABELS: Record<string, string> = {
  en: "EmpatiQ apps",
  uk: "Застосунки EmpatiQ",
  pl: "Aplikacje EmpatiQ",
  ro: "Aplicații EmpatiQ",
};

const LAUNCHER_TITLES: Record<string, string> = {
  en: "What would you like to do?",
  uk: "Що ви хочете зробити?",
  pl: "Co chcesz zrobić?",
  ro: "Ce ai vrea să faci?",
};

const LAUNCHER_HINTS: Record<string, string> = {
  en: "Open an app to begin. Use Desktop to come back.",
  uk: "Відкрийте застосунок, щоб почати. Натисніть «Робочий стіл», щоб повернутися.",
  pl: "Otwórz aplikację, aby zacząć. Wybierz Pulpit, aby wrócić.",
  ro: "Deschide o aplicație pentru a începe. Folosește Desktop pentru a reveni.",
};

function DesktopAppLauncherWidgetImpl(_: WidgetProps) {
  const { locale } = useLanguage();
  const { vertical, verticalConfig, defaultCourseId } = useVertical();
  const apps = verticalConfig.dashboard.desktopApps ?? [];
  const [showHint, setShowHint] = useState(() => !hasSeenDesktopHint());

  // Assigned-course homes have their own focused workspace. The open EmpatiQ
  // home gets the full capability field around Emery.
  if (vertical !== "general" || defaultCourseId || apps.length === 0)
    return null;

  return (
    <nav
      aria-label={LAUNCHER_LABELS[locale] ?? LAUNCHER_LABELS.en}
      className="flex w-full flex-col items-center"
    >
      <div className="mb-2 text-center">
        <h2 className="text-[13px] font-semibold tracking-[-0.01em] text-[var(--ink-secondary)]">
          {LAUNCHER_TITLES[locale] ?? LAUNCHER_TITLES.en}
        </h2>
        {showHint && (
          <p className="mt-1 text-[11px] leading-relaxed text-[var(--ink-muted)]">
            {LAUNCHER_HINTS[locale] ?? LAUNCHER_HINTS.en}
          </p>
        )}
      </div>
      <div className="grid w-full max-w-[920px] grid-cols-3 gap-x-2 gap-y-1 sm:grid-cols-5 sm:gap-x-3 sm:gap-y-2 lg:grid-cols-7">
        {apps.map((item) => {
          const label =
            APP_LABELS[item.id]?.[locale] ??
            APP_LABELS[item.id]?.en ??
            item.label;
          return (
            <button
              key={item.id}
              type="button"
              title={label}
              onClick={() => {
                setShowHint(false);
                openDesktopApp({ ...item, label });
              }}
              className="group flex min-h-24 min-w-0 cursor-pointer flex-col items-center justify-start gap-2 rounded-[16px] px-1.5 py-2.5 text-[var(--ink-secondary)] transition-colors duration-200 hover:bg-white/70 hover:text-[var(--ink-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
            >
              <span
                aria-hidden="true"
                className={`relative grid h-14 w-14 place-items-center overflow-hidden rounded-[16px] border border-white/35 bg-gradient-to-b text-white shadow-[0_10px_22px_-11px_rgba(12,26,51,0.9)] transition-[transform,box-shadow] duration-200 motion-safe:group-hover:-translate-y-1 motion-safe:group-hover:scale-105 motion-reduce:transition-none ${ICON_STYLES[item.id] ?? "from-[var(--accent)] to-[var(--sidebar-bg)]"}`}
              >
                <span
                  aria-hidden="true"
                  className="absolute inset-x-1 top-0 h-px bg-white/45"
                />
                <Icon name={item.icon} size={23} />
              </span>
              <span className="max-w-[108px] text-center text-xs font-medium leading-[1.2]">
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

registerWidget({
  id: "desktopAppLauncher",
  component: DesktopAppLauncherWidgetImpl,
  supportedSlots: new Set(["mainColumn"]),
  displayName: "Desktop app launcher",
});

export const DesktopAppLauncherWidget = DesktopAppLauncherWidgetImpl;
